import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { fileNameEdit } from 'helpers/FileUtils';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/conversion_rate/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData2: [],
  lineChartData: [],
  total: {},
};

const useInvoicesConversionRateVillagePages = (
  isPrint,
  allReportsData,
  isAllReports,
) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <>
        <Table.Summary.Row fixed>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </>
    );
  }, [data]);

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
    },
    {
      title: t('CR YTD'),
      dataIndex: 'CR YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('CR LY'),
      dataIndex: 'CR LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('CRvs LY (+/-%)'),
      dataIndex: 'CR vs LY (+/-%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let data, total;

        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
        } else {
          const { data: backData, total: backTotal } = allReportsData;
          data = backData;
          total = backTotal;
        }

        if (!data) return;

        const tableKeys = Object.keys(total);
        const filteredColumns = columns.filter((column) =>
          tableKeys.includes(column.dataIndex),
        );
        const lineChartData1 = {
          id: 'CR YTD',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['CR YTD'],
          })),
        };
        const lineChartData2 = {
          id: 'CR LY',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['CR LY'],
          })),
        };

        const lineChartData3 = {
          id: 'CR vs LY (+/-%)',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['CR vs LY (+/-%)'],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [lineChartData1, lineChartData2],
          lineChartData2: [lineChartData3],
          total,
        });
        setColumns(filteredColumns);
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          const event = new CustomEvent('status', { detail: 'ok' });

          setTimeout(() => {
            window.dispatchEvent(event);
          }, 5000);
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    summary,
    loading,
    handleDownloadExcel,
    title,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesConversionRateVillagePages;
